import React, { useEffect, useState } from 'react';
import {
  Box, Button, Container, Paper, Stack, TextField, Typography,
} from '@mui/material';
import {
  ArrowBackRounded,
} from '@mui/icons-material';
import {
  Navigate, useLocation, useNavigate, useSearchParams,
} from 'react-router-dom';
import colors from '../../assets/theme/colors';
import { ACTIVE_CHILD_STORAGE_KEY } from '../../components/Dashboard/shared/DashboardViewContext';
import { useRole } from '../../contexts/RoleContext';
import { canEditChildProfile } from '../../constants/roles';
import AskRecordThinkingState from '../../components/AskRecord/AskRecordThinkingState';
import CustomJourneyPlanEditor from '../../features/preparationJourneys/CustomJourneyPlanEditor';
import CustomPreparationDocuments from '../../features/preparationJourneys/CustomPreparationDocuments';
import {
  discardCustomPlanProposal, saveCustomPlanProposal,
} from '../../features/preparationJourneys/customPreparationDraftService';
import { getPreparationDraft } from '../../features/preparationJourneys/preparationDraftService';
import {
  proposeCustomJourneyPlan, startCustomJourneyPlan,
} from '../../services/guidedCareJourneyService';

const makeQuestionId = () => `caregiver_question_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
const proposalStorageKey = (childId) => `lifelog:custom-prepare-proposal:${childId}`;

const readSavedProposal = (childId) => {
  if (typeof window === 'undefined') return null;
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(proposalStorageKey(childId)) || 'null');
    return saved?.proposal?.preparationItems?.length ? saved : null;
  } catch {
    return null;
  }
};

const CustomPreparePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { childrenWithAccess = [], getUserRoleForChild, loading } = useRole();
  const storedChildId = typeof window === 'undefined' ? '' : window.localStorage.getItem(ACTIVE_CHILD_STORAGE_KEY) || '';
  const childId = location.state?.selectedChildId || storedChildId;
  const child = childrenWithAccess.find((item) => item.id === childId) || null;
  const canEdit = canEditChildProfile(getUserRoleForChild?.(childId));
  const [savedProposal] = useState(() => readSavedProposal(childId));
  const [purpose, setPurpose] = useState(savedProposal?.purpose || '');
  const [proposal, setProposal] = useState(savedProposal?.proposal || null);
  const [draftId, setDraftId] = useState(savedProposal?.draftId || '');
  const [working, setWorking] = useState(false);
  const [workingPhase, setWorkingPhase] = useState('');
  const [error, setError] = useState('');
  const resumeDraftId = searchParams.get('draftId') || '';
  const canGenerate = purpose.trim().length >= 10 && !working;
  const canStart = proposal?.preparationItems?.length >= 1 && !working;

  useEffect(() => {
    if (!resumeDraftId || proposal) return undefined;
    let active = true;
    setWorking(true);
    setWorkingPhase('loading');
    getPreparationDraft({ childId, draftId: resumeDraftId })
      .then((draft) => {
        if (!active) return;
        if (!draft?.customPlanProposal || draft.journeyPlan) {
          throw new Error('This preparation plan is no longer available to edit.');
        }
        setPurpose(draft.customPlanProposal.purpose || '');
        setProposal(draft.customPlanProposal);
        setDraftId(draft.id);
      })
      .catch((loadError) => {
        if (active) setError(loadError?.message || "We couldn't reopen this preparation plan.");
      })
      .finally(() => {
        if (active) { setWorking(false); setWorkingPhase(''); }
      });
    return () => { active = false; };
  }, [childId, proposal, resumeDraftId]);

  useEffect(() => {
    if (typeof window === 'undefined' || !proposal) return;
    window.sessionStorage.setItem(proposalStorageKey(childId), JSON.stringify({ purpose, proposal, draftId }));
    if (!draftId) return undefined;
    const timeout = window.setTimeout(() => {
      saveCustomPlanProposal({ childId, draftId, proposal }).catch(() => {});
    }, 500);
    return () => window.clearTimeout(timeout);
  }, [childId, draftId, proposal, purpose]);

  const generate = async () => {
    setWorking(true); setWorkingPhase('generating'); setError('');
    try {
      const result = await proposeCustomJourneyPlan({ childId, purpose: purpose.trim() });
      setProposal(result.proposal);
      setDraftId(result.draftId || '');
    } catch (requestError) {
      setError(requestError?.message || "We couldn't create a preparation plan right now.");
    } finally { setWorking(false); setWorkingPhase(''); }
  };
  const start = async () => {
    setWorking(true); setWorkingPhase('starting'); setError('');
    try {
      const result = await startCustomJourneyPlan({ childId, proposal, draftId });
      window.sessionStorage.removeItem(proposalStorageKey(childId));
      navigate('/dashboard', {
        state: {
          selectedChildId: childId,
          childShellTarget: 'tools',
          preparationStarted: { draftId: result.draftId, route: result.route, title: proposal.title },
        },
      });
    } catch (requestError) {
      setError(requestError?.message || "We couldn't start this preparation right now.");
    } finally { setWorking(false); setWorkingPhase(''); }
  };
  const startOver = async () => {
    setWorking(true); setWorkingPhase('discarding'); setError('');
    try {
      if (draftId) await discardCustomPlanProposal({ childId, draftId });
      window.sessionStorage.removeItem(proposalStorageKey(childId));
      setProposal(null);
      setDraftId('');
      if (resumeDraftId) navigate('/prepare', { replace: true, state: { selectedChildId: childId } });
    } catch (discardError) {
      setError(discardError?.message || "We couldn't discard this preparation plan.");
    } finally { setWorking(false); setWorkingPhase(''); }
  };

  if (!loading && (!child || !canEdit)) return <Navigate to="/dashboard" replace />;
  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: colors.semantic.sageMist }}>
      <Container maxWidth={proposal ? 'md' : 'sm'} sx={{ py: { xs: 2, md: 3 } }}>
        <Button onClick={() => navigate(-1)} startIcon={<ArrowBackRounded />} sx={{ mb: 1.5 }}>Back</Button>
        <Paper elevation={0} sx={proposal ? { p: 0, bgcolor: 'transparent' } : { p: { xs: 2, md: 3 }, borderRadius: '24px' }}>
          {!proposal ? (
            <Stack spacing={2}>
              <Typography component="h1" sx={{ fontSize: '1.75rem', fontWeight: 800 }}>What are you preparing for?</Typography>
              <Typography color="text.secondary">Tell Lifelog about the meeting, conversation, or care handover.</Typography>
              <TextField multiline minRows={5} value={purpose} onChange={(event) => setPurpose(event.target.value)} placeholder="For example, I’m meeting Maya’s neurologist because she has been very tired." inputProps={{ maxLength: 1200 }} />
              {working ? <AskRecordThinkingState compact firstName={child?.name} /> : null}
              <Button variant="contained" disabled={!canGenerate} onClick={generate} sx={{ minHeight: 52 }}>Create a preparation plan</Button>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Paper elevation={0} sx={{ p: { xs: 1.8, md: 2.5 }, borderRadius: '24px' }}>
                <Typography component="h1" sx={{ fontSize: { xs: '1.5rem', md: '1.8rem' }, fontWeight: 800 }}>Here’s how Lifelog can help you prepare</Typography>
                <TextField sx={{ mt: 1.6 }} fullWidth label="Preparation title" value={proposal.title} onChange={(event) => setProposal((current) => ({ ...current, title: event.target.value }))} />
                <Typography sx={{ mt: 1, color: 'text.secondary' }}>Review, reorder, edit, or remove anything before you start. Lifelog will prepare record-based information after the plan is checked.</Typography>
              </Paper>
              <CustomJourneyPlanEditor proposal={proposal} onChange={setProposal} makeQuestionId={makeQuestionId} />
              <CustomPreparationDocuments childId={childId} draftId={draftId} />
              {working ? (
                <Box>
                  <Typography role="status" sx={{ mb: 0.5, fontWeight: 750 }}>
                    {workingPhase === 'starting' ? 'Checking your preparation plan…' : 'Creating your preparation plan…'}
                  </Typography>
                  <AskRecordThinkingState compact firstName={child?.name} />
                </Box>
              ) : null}
              <Button variant="contained" disabled={!canStart || !draftId || proposal.preparationItems.some((item) => !item.label.trim())} onClick={start} sx={{ minHeight: 54 }}>Start preparing</Button>
              <Button disabled={working} onClick={startOver}>Start over</Button>
            </Stack>
          )}
          {error ? <Typography role="alert" sx={{ mt: 1.5, color: '#8F3333' }}>{error}</Typography> : null}
        </Paper>
      </Container>
    </Box>
  );
};

export default CustomPreparePage;
